import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    CHAT_USER_EXPIRY_SWEEP_INTERVAL_MS,
    pruneExpiredChatUsers,
} from '@apps/web/hooks/chat_expiring_users';
import { useListenForOrganizationEvent } from '@apps/web/hooks/use_listen_for_organization_event';
import { getApiClient } from '@apps/web/lib/api_client';

// Throttle "still typing" pings so a fast typist sends at most one POST per
// window, and auto-clear a remote typer shortly after their last ping so a
// dropped client never leaves a stuck "is typing…" indicator.
const TYPING_RESEND_INTERVAL_MS = 3000;
const TYPING_STOP_DEBOUNCE_MS = 3000;
const TYPING_EXPIRY_MS = 5000;

export type ChatTypingUser = {
    readonly userId: string;
    readonly displayName: string;
};

export function formatChatTypingLabel(
    typingUsers: ReadonlyArray<{ readonly displayName: string }>,
): string {
    const names = typingUsers.map((typingUser) => typingUser.displayName);
    if (names.length === 1) {
        return `${names[0]} is typing…`;
    }
    if (names.length === 2) {
        return `${names[0]} and ${names[1]} are typing…`;
    }
    const otherCount = names.length - 2;
    return `${names[0]}, ${names[1]}, and ${otherCount} ${
        otherCount === 1 ? 'other' : 'others'
    } are typing…`;
}

type TypingEntry = {
    readonly displayName: string;
    readonly expiresAt: number;
};

export function usePhoebeChatTyping({
    organizationId,
    runId,
    currentUserId,
    enabled,
}: {
    readonly organizationId: string | null;
    readonly runId: string | null;
    readonly currentUserId: string | null;
    readonly enabled: boolean;
}): {
    readonly typingUsers: Array<ChatTypingUser>;
    readonly notifyTyping: () => void;
} {
    const [typingByUserId, setTypingByUserId] = useState<
        Record<string, TypingEntry>
    >({});
    const lastTrueSentAtMsRef = useRef(0);
    const stopTimerRef = useRef<number | null>(null);

    const sendTyping = useCallback(
        (typing: boolean) => {
            if (!enabled || !organizationId || !runId) {
                return;
            }
            void getApiClient()
                .post({
                    url: '/organizations/{organization_id}/runs/{run_id}/typing',
                    pathParams: {
                        organization_id: organizationId,
                        run_id: runId,
                    },
                    body: { typing },
                    bestEffort: true,
                })
                .catch(() => {
                    // Presence is best-effort; a dropped ping self-heals via the
                    // remote expiry sweep, so failures are intentionally ignored.
                });
        },
        [enabled, organizationId, runId],
    );

    const notifyTyping = useCallback(() => {
        if (!enabled || !organizationId || !runId) {
            return;
        }
        const nowMs = Date.now();
        if (nowMs - lastTrueSentAtMsRef.current > TYPING_RESEND_INTERVAL_MS) {
            lastTrueSentAtMsRef.current = nowMs;
            sendTyping(true);
        }
        if (stopTimerRef.current !== null) {
            window.clearTimeout(stopTimerRef.current);
        }
        stopTimerRef.current = window.setTimeout(() => {
            lastTrueSentAtMsRef.current = 0;
            sendTyping(false);
        }, TYPING_STOP_DEBOUNCE_MS);
    }, [enabled, organizationId, runId, sendTyping]);

    // Announce that we stopped typing when the chat unmounts or the run
    // changes, so teammates don't see a lingering indicator.
    useEffect(() => {
        return () => {
            if (stopTimerRef.current !== null) {
                window.clearTimeout(stopTimerRef.current);
                stopTimerRef.current = null;
            }
            if (lastTrueSentAtMsRef.current !== 0) {
                lastTrueSentAtMsRef.current = 0;
                sendTyping(false);
            }
        };
    }, [sendTyping]);

    useListenForOrganizationEvent(
        'chat_typing',
        (event) => {
            if (event.type !== 'chat_typing') {
                return;
            }
            const data = event.data;
            if (data.run_id !== runId || data.user_id === currentUserId) {
                return;
            }
            setTypingByUserId((previous) => {
                const next = { ...previous };
                if (data.typing) {
                    next[data.user_id] = {
                        displayName: data.display_name,
                        expiresAt: Date.now() + TYPING_EXPIRY_MS,
                    };
                } else {
                    delete next[data.user_id];
                }
                return next;
            });
        },
        { organizationId },
    );

    // Reset presence when the viewed run (or org) changes, so typers from a
    // previously-open chat don't linger in the new one until their TTL expires.
    useEffect(() => {
        setTypingByUserId({});
    }, [runId, organizationId]);

    // Drop expired typers so a missed "stopped" ping cannot wedge the indicator.
    // Gate the sweep on whether ANY typer is present (a boolean) rather than on
    // the map identity, so the interval starts when the first typer appears and
    // stops when the last clears — it ticks steadily instead of restarting on
    // every individual expiry.
    const hasTypers = Object.keys(typingByUserId).length > 0;
    useEffect(() => {
        if (!hasTypers) {
            return;
        }
        const intervalId = window.setInterval(() => {
            setTypingByUserId((previous) => {
                return pruneExpiredChatUsers(previous, Date.now());
            });
        }, CHAT_USER_EXPIRY_SWEEP_INTERVAL_MS);
        return () => window.clearInterval(intervalId);
    }, [hasTypers]);

    const typingUsers = useMemo(
        () =>
            Object.entries(typingByUserId).map(([userId, entry]) => ({
                userId,
                displayName: entry.displayName,
            })),
        [typingByUserId],
    );

    return { typingUsers, notifyTyping };
}
